import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { marketImageUrl, marketTitle } from '../../lib/marketCatalog';
import { cleanTiers, firstTierQty, formatSar, orderStepOf, toSar } from '../../lib/marketPricing';
import { useFavorites } from '../../lib/marketFavorites';
import { compareIds, COMPARE_MAX, onMarketLocalChange, toggleCompare } from '../../lib/marketLocal';
import StarRating from './StarRating';
import { useDeliveredConfig } from '../../lib/marketMode';
import { DELIVERED_T, productCardPrice, productPrice, staffShipping, useDeliveredInfo } from '../../lib/marketDelivered';
import { Icon } from './MarketShell';
import SupplierName from './SupplierName';

// One marketplace product card — every listing, rail, related block and the
// favorites page use it, so a product reads the same everywhere. Everything on
// it is the supplier's own data: the price range in SAR (marketPricing), the
// real minimum (rounded up to the ordering step), the wholesale price from the
// supplier's own tiers, its rating only when it has a valid one (never
// invented, never a MAABAR verification). No discounts, sales counts or
// badges that the data does not carry.

export const CARD_T = {
  ar: { startOne: 'ابدأ من قطعة', startN: (n) => `ابدأ من ${n} قطع`, cheaper: 'أرخص بالكميات', rating: 'تقييم المورد', moq: 'الحد الأدنى', from: 'من', tier: (p, q) => `${p} عند طلب ${q}+`, perUnit: 'للوحدة',
    fav: 'أضف إلى المفضلة', unfav: 'إزالة من المفضلة', favFailed: 'تعذّر حفظ المفضلة، حاول مرة أخرى.', compare: 'أضف إلى المقارنة', comparing: 'في المقارنة', compareFull: `يمكن مقارنة ${COMPARE_MAX} منتجات كحد أقصى`, view: 'عرض المنتج' },
  en: { startOne: 'From one piece', startN: (n) => `From ${n} pieces`, cheaper: 'Cheaper in quantity', rating: 'Supplier rating', moq: 'Min. order', from: 'From', tier: (p, q) => `${p} for ${q}+`, perUnit: '/ unit',
    fav: 'Add to favorites', unfav: 'Remove from favorites', favFailed: 'Could not save to favorites — please try again.', compare: 'Add to comparison', comparing: 'In comparison', compareFull: `Up to ${COMPARE_MAX} products can be compared`, view: 'View product' },
  zh: { startOne: '1 件起订', startN: (n) => `${n} 件起订`, cheaper: '量大更便宜', rating: '供应商评分', moq: '起订量', from: '起', tier: (p, q) => `${q}+ 件 ${p}`, perUnit: '/ 件',
    fav: '加入收藏', unfav: '取消收藏', favFailed: '收藏未能保存，请重试。', compare: '加入对比', comparing: '对比中', compareFull: `最多可对比 ${COMPARE_MAX} 个商品`, view: '查看商品' },
};

export const minOrderQty = (p) => { const step = orderStepOf(p); return Math.ceil(Math.max(1, Number(p.moq) || 1, firstTierQty(p)) / step) * step; };
export const unitWord = (p, lang) => (p.sale_unit ? (lang === 'ar' ? p.sale_unit.ar : lang === 'zh' ? p.sale_unit.zh : p.sale_unit.en) || '' : '');
export const mainImageRef = (p) => { const imgs = Array.isArray(p.images) ? p.images : []; return (imgs.find((i) => i.type === 'main') || imgs[0] || {}).ref || null; };
export const mainImage = (p, thumb = true) => marketImageUrl(mainImageRef(p), thumb);

/** The deepest wholesale price the supplier states, when one price covers every variant. */
export function wholesaleHint(p, rates) {
  if (Number(p.price_min_cny) !== Number(p.price_max_cny)) return null;   // per-variant pricing: tiers are not one price
  const t = cleanTiers(p.price_tiers);
  if (t.length < 2) return null;
  const last = t[t.length - 1];
  const sar = toSar(last.price_cny, rates);
  return sar != null ? { sar, qty: last.min_quantity } : null;
}

export function useCompareIds() {
  const [ids, setIds] = useState(() => compareIds());
  useEffect(() => onMarketLocalChange(() => setIds(compareIds())), []);
  return ids;
}

export function FavoriteButton({ productId, user, lang = 'ar', className = 'mk-icon-btn' }) {
  const nav = useNavigate();
  const c = CARD_T[lang] || CARD_T.ar;
  const fav = useFavorites(user?.id);
  const on = fav.isFav(productId);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const click = async (e) => {
    e.stopPropagation();
    if (!user) { nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`); return; }
    setBusy(true); setFailed(false);
    const r = await fav.toggle(productId);
    setBusy(false);
    // Never silent: a save that did not happen says so (and the heart stays as it was).
    if (!r.ok) {
      console.warn('[favorites] toggle failed:', r.error);
      setFailed(true);
      setTimeout(() => setFailed(false), 3500);
    }
  };
  return (
    <span className="mk-fav-wrap">
      <button type="button" className={className} data-fav={on ? 1 : 0} data-fav-error={failed ? 1 : undefined} aria-pressed={on}
        aria-label={on ? c.unfav : c.fav} title={failed ? c.favFailed : on ? c.unfav : c.fav} disabled={busy} onClick={click}>
        <Icon.Heart filled={on} />
      </button>
      {failed && <span className="mk-fav-err" role="alert">{c.favFailed}</span>}
    </span>
  );
}

/** Compare as an icon button (cards) — `labelled` renders the text version for the product page. */
export function CompareToggle({ productId, lang = 'ar', labelled = false }) {
  const c = CARD_T[lang] || CARD_T.ar;
  const ids = useCompareIds();
  const on = ids.includes(productId);
  const [full, setFull] = useState(false);
  const click = (e) => { e.stopPropagation(); const r = toggleCompare(productId); setFull(r.full); };
  if (labelled) {
    return (
      <button type="button" className="mk-btn ghost" data-compare={on ? 1 : 0} aria-pressed={on} onClick={click} style={{ height: 38, fontSize: 13 }}>
        <span style={{ width: 16, height: 16, display: 'inline-flex' }}><Icon.Compare /></span>{full ? c.compareFull : on ? c.comparing : c.compare}
      </button>
    );
  }
  return (
    <button type="button" className="mk-icon-btn cmp" data-compare={on ? 1 : 0} aria-pressed={on} aria-label={full ? c.compareFull : on ? c.comparing : c.compare}
      title={full ? c.compareFull : on ? c.comparing : c.compare} onClick={click}>
      <Icon.Compare />
    </button>
  );
}

export default function MarketProductCard({ p, lang = 'ar', user, exchangeRates }) {
  const c = CARD_T[lang] || CARD_T.ar;
  const href = `/market/${p.id}`;
  const lo = toSar(p.price_min_cny, exchangeRates);
  const minQty = minOrderQty(p);
  const title = marketTitle(p, lang);
  // The delivered-price model (owner, 2026-10-29): «واصل لك» with the air
  // delivered unit price as the hero (always available), sea «بالكميات» as a
  // green second line, the supplier with ✓, and the minimum order in pieces
  // and riyals.
  const dcfg = useDeliveredConfig();
  const dinfo = useDeliveredInfo(p.id, !!dcfg);
  // the PRODUCT price only — the lowest across variants (owner, 2026-11-02 final display decision);
  // shipping and customs appear in the cart
  const dp = dcfg && dinfo ? productCardPrice(p, dinfo, dcfg, (cny) => toSar(cny, exchangeRates)) : null;
  // «ابدأ من قطعة · أرخص بالكميات» (owner, 2026-11-02): the minimum in pieces; «cheaper in quantity» only with the supplier's real price tiers
  // the green «ينزل لـ X بالكميات» line already says it gets cheaper (owner, 2026-11-02): this line is the start only
  const startLine = minQty === 1 ? c.startOne : c.startN(minQty.toLocaleString('en-US'));
  const dt = DELIVERED_T[lang] || DELIVERED_T.ar;
  // a product whose shipping staff price: the same product price, and their line under it
  const confirm = !!dp?.staff;
  return (
    <article className="mk-card" data-market-card={p.id} data-card-staff={staffShipping(dinfo) ? '1' : undefined}>
      {/* the whole card is ONE real link (owner, 2026-11-02): the title's <a> stretches over the card (.mk-card-link::after);
          the heart, compare and the supplier name sit above it and are not inside it — no button inside an <a> */}
      <div className="mk-card-media">
        <img src={mainImage(p) || ''} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
      </div>
      <div className="mk-card-tools">
        <FavoriteButton productId={p.id} user={user} lang={lang} />
        <CompareToggle productId={p.id} lang={lang} />
      </div>
      <div className="mk-card-body">
        <h3 className="mk-card-title" title={title}><Link className="mk-card-link" to={href} data-card-link={p.id}>{title}</Link></h3>
        {dp ? (
          <>
            <p className="mk-price" data-card-price={dp.price} data-card-product-price="1">
              <span className="mk-ltr">{formatSar(dp.price)}</span><small> {dt.perUnit}</small>
            </p>
            {/* one line under the price: shipping is worked out in the cart (owner, 2026-11-02), or staff price it */}
            {confirm
              ? <p className="mk-card-air" data-card-confirm-line="1">{dt.confirmLine(dcfg?.confirm_hours ?? 48)}</p>
              : <p className="mk-card-dlabel" data-card-ship-in-cart="1" title={dt.shipInCart}>{dt.shipInCart}</p>}
            {/* «يبدأ من X بالكميات»: the supplier's last price break, as a product price */}
            {dp.low != null && <p className="mk-card-sea" data-card-low={dp.low}>{dt.dropsTo(formatSar(dp.low))}</p>}
          </>
        ) : (
          // before this product's figures arrive (or without them): never the factory's raw price
          // (owner, 2026-11-02 — the fee, the allowance and the rounding always apply). The card's own
          // lowest price as a product price; «—» only while the pricing config itself is loading.
          <p className="mk-price" data-card-price={dcfg && lo != null ? productPrice(lo, dcfg) : ''} data-card-product-price={dcfg ? '1' : undefined}>
            <span className="mk-ltr">{dcfg && lo != null ? formatSar(productPrice(lo, dcfg)) : '—'}</span>
            {dcfg && lo != null && <small> {dt.perUnit}</small>}
          </p>
        )}
        {/* rule B: a sea hero priced from the sea minimum says from how many pieces, not a minimum-order total it cannot ship */}
        <p className="mk-moq" data-card-moq={minQty} data-card-start={minQty}>{startLine}</p>
        <div className="mk-card-foot">
          {p.seller_name_latin ? <SupplierName className="mk-seller" name={p.seller_name_latin} prefix={dp ? '✓ ' : ''} /> : <span />}
          <StarRating rating={p.seller_rating} size={11} compact outOf="/5" label={c.rating} />
        </div>
      </div>
    </article>
  );
}
